import { navigate } from 'gatsby';
import { useEffect, useMemo, useState } from 'react';

import {
  generateLocationQueryString,
  getLocationQueryStringParam,
  isBrowser,
} from '~/utils/browser';
import { QUERY_STRING_PARAM_STRATEGY } from '~/utils/constants';

import { UseLoadMoreParams } from './models';

export const useLoadMore = <T extends unknown>({
  componentId,
  items,
  itemsPerPage,
}: UseLoadMoreParams<T>) => {
  let queryParam = `page`;
  if (componentId) {
    queryParam = `${componentId}-${queryParam}`;
  }
  const [visibleItems, setVisibleItems] = useState(items);

  // Always start at page 1 — the load-more pattern is accumulative and cannot
  // meaningfully restore mid-list state from the URL (the API fetch hasn't
  // happened yet), so reading ?page=N on mount would hide the "Show More"
  // button and show an incomplete list after a browser back-navigation.
  const [page, setPage] = useState<number>(1);

  const nextPageButtonVisible = useMemo(
    () =>
      itemsPerPage !== undefined &&
      page < Math.ceil(items.length / itemsPerPage),
    [items, itemsPerPage, page]
  );

  // On mount, silently remove any stale ?page= param left in the URL by a
  // previous "Show More" click (e.g. after a browser back-navigation) so the
  // address bar stays in sync with the reset page state.
  useEffect(() => {
    const storedPage = getLocationQueryStringParam(
      queryParam,
      QUERY_STRING_PARAM_STRATEGY.STRING
    ) as string;

    if (storedPage && parseInt(storedPage, 10) > 1 && isBrowser()) {
      window.history.replaceState(
        null,
        '',
        generateLocationQueryString({ [queryParam]: null })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (page > 1) {
      navigate(generateLocationQueryString({ page: page.toString() }), {
        state: { noScroll: true },
      });
    }
  }, [page]);

  useEffect(() => {
    if (itemsPerPage) {
      setVisibleItems(items.slice(0, page * itemsPerPage));

      return;
    }

    setVisibleItems(items);
  }, [items, itemsPerPage, page]);

  const nextPage = () => {
    setPage((current) => current + 1);
  };

  const previousPage = () => {
    setPage((current) => current - 1);
  };

  return {
    visibleItems,
    nextPageButtonVisible,
    nextPage,
    previousPage,
    setPage,
  };
};
